import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { DateTimeService } from "../../shared/datetime/datetime.service";
import { GenericModalService } from "../../shared/generic-modal/generic-modal.service";
import { BizDataGridDirective } from "../../shared/grid/biz-grid.directive";
import { LoadingPanelService, LoadingPanelSettings } from "../../shared/loading-panel/loading-panel.service";
import { CurrentUserService } from "../../user/services/current-user.service";
import { StatusEventSearchItem } from "../models/status-event-search-item";
import { TrackingService } from "../services/tracking.service";

import { PermissionRequiredDirective } from "../../shared/authentication/permission-required.directive";
import { DxDataGridModule } from "devextreme-angular";
import { DxoMasterDetailModule, DxoStateStoringModule, DxiColumnModule } from "devextreme-angular/ui/nested";
import { DxTemplateModule } from "devextreme-angular/core";
import { StatusEventsDetailGridComponent } from "./status-events-detail-grid.component";

@Component({
    selector: 'app-status-events-master-grid',
    templateUrl: './status-events-master-grid.component.html',
    styleUrls: ['./status-events-master-grid.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PermissionRequiredDirective, DxDataGridModule, BizDataGridDirective, DxoMasterDetailModule, DxoStateStoringModule, DxiColumnModule, DxTemplateModule, StatusEventsDetailGridComponent]
})

export class StatusEventsMasterGridComponent implements AfterViewInit  {
    
    constructor(
        public dateTimeService: DateTimeService,
        private modalService: GenericModalService,
        private loadingPanelService: LoadingPanelService,
        private trackingService: TrackingService,
        private router: Router,
        private currentUserService: CurrentUserService) { }
    
    selectedDataSourceItems: StatusEventSearchItem[] = [];

    @ViewChild(BizDataGridDirective, { static: true }) private datagrid: BizDataGridDirective;

    @Input() dataSourceItems: StatusEventSearchItem[] = [];
    @Input() hideShowDocumentsButton: boolean = false;
    @Output() onRefreshEvent: EventEmitter<void> = new EventEmitter<void>();

    ngAfterViewInit(): void {
        this.datagrid.rowDoubleClick.subscribe((row) => {
            if (this.currentUserService.hasPermission('Tracking_View_DocumentDetails')) {
                this.showGivenDocuments([row]);
            }
        });
    }

    onRefresh() {
        if (this.onRefreshEvent) {
            this.onRefreshEvent.emit();
        }
    }

    selectionChanged(e: any) {
        this.selectedDataSourceItems = e.selectedRowsData;
    }

    async reprocess() {
        try {
            const comment = await this.modalService.inputDirect('Reprocess', 'Are you sure you want to reprocess the selected status events?  <br /><br />Please enter a comment:', undefined, undefined, true);
            try {
                this.loadingPanelService.showLoadingPanel(new LoadingPanelSettings('Setting status on status event(s)'))
                const runIds = this.selectedDataSourceItems.map<number>((r) => r.StatusEventSubscriptionRunId);
                this.trackingService.reprocessStatusEventRuns(runIds, comment).then(() => {
                    this.loadingPanelService.hideLoadingPanel();
                    this.onRefresh();
                });
            }
            catch (error) {
                console.error(error);
                this.loadingPanelService.hideLoadingPanel();
            }
        } catch (error) {
            console.error(error);
        }
    }

    async handleManually() {
        try {
            const comment = await this.modalService.inputDirect('Manually handle', 'Are you sure you want to manually handled the selected status events(s)?  <br /><br />Please enter a comment:', undefined, undefined, true);
            try {
                this.loadingPanelService.showLoadingPanel(new LoadingPanelSettings('Setting status on status event(s)'))
                const runIds = this.selectedDataSourceItems.map<number>((r) => r.StatusEventSubscriptionRunId);
                this.trackingService.manuallyHandleEventRuns(runIds, comment).then(() => {
                    this.loadingPanelService.hideLoadingPanel();
                    this.onRefresh();
                });
            }
            catch (error) {
                console.error(error);
                this.loadingPanelService.hideLoadingPanel();
            }
        } catch (error) {
            console.error(error);
        }
    }

    showDocuments() {
        this.showGivenDocuments(this.selectedDataSourceItems);
    }

    showGivenDocuments(statusEventsToShowDetailsOf: StatusEventSearchItem[]) {
        const documentIds = statusEventsToShowDetailsOf
            .filter(r => r.DocumentId !== undefined && r.DocumentId !== null)
            .map<number>(r => r.DocumentId)
            .filter((val, idx, self) => self.indexOf(val) === idx); // distinct;

        const interchangeInIds = statusEventsToShowDetailsOf
            .filter(r => r.DocumentId === undefined || r.DocumentId === null)
            .map<number>(r => r.InterchangeInId)
            .filter((val, idx, self) => self.indexOf(val) === idx); // distinct;

        if (documentIds.length > 0 || interchangeInIds.length > 0) {

            if ((documentIds.length + interchangeInIds.length) > TrackingService.maxNumberOfDocumentsToShow) {
                const maxDocuments = TrackingService.maxNumberOfDocumentsToShow;
                this.modalService.messageDirect('Maximum number of documents', `Maximum number of documents to show is ${maxDocuments}`);
            }
            else {
                const searchIdObject = {};

                if (documentIds.length > 0) {
                    searchIdObject['documentIds'] = documentIds.join(',');
                }

                if (interchangeInIds.length > 0) {
                    searchIdObject['interchangeInIds'] = interchangeInIds.join(',');
                }

                this.router.navigate(['tracking/result/details', searchIdObject]);
            }
        }
    }
}
